import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches a list of RCTIs from the backend.
 * @param {object} [options={}] - Optional parameters for filtering, pagination, and sorting.
 * @param {string} [options.status] - Filter by RCTI status.
 * @param {number} [options.pageSize=10] - The number of RCTIs to fetch per page.
 * @param {string} [options.orderBy='RCTIDate'] - Field to order by.
 * @param {'asc'|'desc'} [options.orderDirection='desc'] - Direction of ordering.
 * @param {string} [options.fseId] - Filter by a specific franchisee ID.
 * @param {string} [options.jobId] - Filter RCTIs related to a specific job ID.
 * @param {string} [options.clientId] - Filter RCTIs related to a specific client ID.
 * @param {string} [options.householdId] - Filter RCTIs related to a specific household ID.
 * @param {string} [options.startAfterDocId] - The ID of the last RCTI from the previous page.
 * @returns {Promise<object>} - The response data containing RCTIs and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listRCTIs = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    pageSize = 10,
    orderBy = 'RCTIDate',
    orderDirection = 'desc',
    fseId,
    fseCode,
    jobId,
    clientId,
    householdId,
    startAfterDocId,
    status,
    xeroStatus,
    rctiNumber,
    imported,
    importSource,
    manual,
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/list`;

    const body = { pageSize, orderBy, orderDirection };

    if (status !== undefined) {
      body.status = status;
    }
    
    if (fseCode) body.fseCode = fseCode;
    if (fseId) body.fseId = fseId;
    if (fseCode) body.fseCode = fseCode;
    if (jobId) body.jobId = jobId;
    if (clientId) body.clientId = clientId;
    if (householdId) body.householdId = householdId;
    if (startAfterDocId) body.startAfterDocId = startAfterDocId;
    if (xeroStatus) body.xeroStatus = xeroStatus;
    if (rctiNumber) body.rctiNumber = rctiNumber;
    if (imported !== undefined) body.imported = imported;
    if (importSource) body.importSource = importSource;
    if (manual !== undefined) body.manual = manual;

    console.log("Sending request to RCTI API:", { url, body });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list RCTIs');
    }
    return responseData.data; // Expected: { rctis: [], nextPageDocId: '...' }
  } catch (error) {
    console.error("Error listing RCTIs:", error);
    throw error;
  }
};

/**
 * Fetches details for a specific RCTI by ID.
 * @param {string} rctiId - The ID of the RCTI to fetch.
 * @returns {Promise<object>} - The RCTI data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getRCTIDetails = async (rctiId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/details`;
    const payload = { rctiId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to fetch RCTI details');
    }

    // Log the complete response data to debug field mapping issues
    console.log('🔍 RCTI Details Response:', {
      rctiId,
      responseData,
      dataKeys: Object.keys(responseData.data || {}),
      contactFields: {
        ContactName: responseData.data?.ContactName,
        XeroContactId: responseData.data?.XeroContactId,
        ContactEmail: responseData.data?.ContactEmail,
        EmailAddress: responseData.data?.EmailAddress,
        // Check for potential variations
        contactName: responseData.data?.contactName,
        xeroContactId: responseData.data?.xeroContactId,
        contactEmail: responseData.data?.contactEmail,
        emailAddress: responseData.data?.emailAddress,
      }
    });

    return responseData.data; // Return the RCTI data object
  } catch (error) {
    console.error(`Error fetching RCTI ${rctiId}:`, error);
    throw error;
  }
};

/**
 * Updates editable line item values for a draft RCTI.
 * @param {string} rctiId - The Firestore RCTI ID.
 * @param {{Quantity: number|string, UnitAmount: number|string}} updates - Updated line item values.
 * @returns {Promise<object>} - The response payload from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const updateRCTILineItem = async (rctiId, updates) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/update-line-item`;
    const payload = {
      rctiId,
      Quantity: updates?.Quantity,
      UnitAmount: updates?.UnitAmount,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to update RCTI line item');
    }

    return responseData;
  } catch (error) {
    console.error(`Error updating RCTI ${rctiId} line item:`, error);
    throw error;
  }
};

/**
 * Admin: update an RCTI's unit price and/or quantity. Unlike updateRCTILineItem,
 * this also syncs the linked job's FSE price, regenerates the PDF, void+recreates
 * any synced Xero bill (refusing if it is paid) and resends the RCTI email.
 * Backed by the admin-gated POST /rcti/admin-update-pricing endpoint.
 *
 * @param {string} rctiId
 * @param {{unitPrice?: number|null, quantity?: number|null, skipXero?: boolean}} updates
 * @returns {Promise<object>} - The full result payload ({ status, message, plan, steps, warnings }).
 * @throws {Error} - Throws (with the server message) on non-2xx, e.g. a paid Xero bill.
 */
export const adminUpdateRCTIPricing = async (rctiId, updates = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/admin-update-pricing`;
    const payload = {
      rctiId,
      unitPrice: updates?.unitPrice,
      quantity: updates?.quantity,
      skipXero: updates?.skipXero === true,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to update RCTI pricing');
    }

    return responseData;
  } catch (error) {
    console.error(`Error updating RCTI ${rctiId} pricing:`, error);
    throw error;
  }
};

/**
 * Regenerates the PDF for an existing RCTI: rebuilds the file, refreshes the signed
 * download link and writes pdfDownloadUrl back to the record. Does NOT touch Xero.
 * Backed by POST /rcti/generate-pdf with forceRegenerate.
 * @param {string} rctiId - The Firestore RCTI ID.
 * @returns {Promise<object>} - The response payload ({ status, message, data }).
 * @throws {Error} - Throws (with the server message) on failure or if unauthenticated.
 */
export const regenerateRCTIPdf = async (rctiId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/generate-pdf`;
    const payload = { rctiId, forceRegenerate: true };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to regenerate RCTI PDF');
    }

    return responseData;
  } catch (error) {
    console.error(`Error regenerating RCTI ${rctiId} PDF:`, error);
    throw error;
  }
};

/**
 * Sends a draft RCTI to Xero.
 * @param {string} draftRCTIId - The ID of the draft RCTI in Firestore.
 * @returns {Promise<object>} - The response data from the Xero API integration.
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated.
 */
export const sendDraftRCTIToXero = async (draftRCTIId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!draftRCTIId) {
    throw new Error("Draft RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/create-from-draft`;
    const payload = { draftRCTIId };

    console.log("Sending draft RCTI to Xero, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (!responseData.success) { 
        throw new Error(responseData.message || 'Failed to send RCTI to Xero');
    }

    return responseData;
  } catch (error) {
    console.error(`Error sending draft RCTI ${draftRCTIId} to Xero:`, error);
    throw error;
  }
};

/**
 * Sends multiple draft RCTIs to Xero.
 * @param {string[]} draftRCTIIds - An array of draft RCTI IDs.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated.
 */
export const sendMultipleDraftRCTIsToXero = async (draftRCTIIds) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!draftRCTIIds || draftRCTIIds.length === 0) {
    throw new Error("Draft RCTI IDs are required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/batch-from-draft`;
    const payload = { draftRCTIIds };

    console.log("Sending multiple draft RCTIs to Xero, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok && response.status !== 207 && response.status !== 400) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }
    
    return responseData; 

  } catch (error) {
    console.error('Error sending multiple draft RCTIs to Xero:', error);
    throw error;
  }
};

/**
 * Syncs an RCTI with Xero to get the latest status and payment information
 * @param {string} rctiId - The Firestore RCTI ID
 * @returns {Promise<object>} - The response data containing sync status and updated fields
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const syncRCTIWithXero = async (rctiId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/sync-with-xero`;
    const payload = { rctiId };

    console.log("Syncing RCTI with Xero, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error(`Error syncing RCTI ${rctiId} with Xero:`, error);
    throw error;
  }
};

/**
 * Manually links an RCTI to an existing Xero bill (created by hand in Xero).
 * @param {string} rctiId - The Firestore RCTI ID.
 * @param {string} xeroInvoiceId - A Xero InvoiceID or go.xero.com URL.
 * @returns {Promise<object>} - The response data containing the link result.
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated.
 */
export const linkRCTIToXeroBill = async (rctiId, xeroInvoiceId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }
  if (!xeroInvoiceId) {
    throw new Error("Xero bill ID or URL is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/link-xero-bill`;
    const payload = { rctiId, xeroInvoiceId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error(`Error linking RCTI ${rctiId} to Xero bill ${xeroInvoiceId}:`, error);
    throw error;
  }
};

/**
 * Syncs multiple RCTIs with Xero to get their latest status and payment information.
 * @param {string[]} rctiIds - Array of Firestore RCTI IDs to sync
 * @returns {Promise<object>} - The response containing sync results for each RCTI
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const syncMultipleRCTIsWithXero = async (rctiIds) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiIds?.length) {
    throw new Error("At least one RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/batch-sync-with-xero`;
    const payload = { rctiIds };

    console.log('📤 RCTI Sync Request:', {
      url,
      method: 'POST',
      payload
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    // For 204 No Content, return a standardized response
    if (response.status === 204) {
      const standardResponse = {
        status: 'success',
        message: 'All RCTIs are up to date with Xero.',
        results: {
          totalProcessed: rctiIds.length,
          totalUpdated: 0,
          totalFailed: 0,
          totalSkipped: rctiIds.length,
          details: []
        },
        responseStatus: 204
      };
      console.log('📥 RCTI Sync Response (204):', standardResponse);
      return standardResponse;
    }

    const responseData = await response.json();
    console.log(`📥 RCTI Sync Response (${response.status}):`, responseData);

    if (!response.ok && response.status !== 207) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    // Include the HTTP status in the response
    return {
      ...responseData,
      responseStatus: response.status
    };
  } catch (error) {
    console.error('❌ Error syncing RCTIs with Xero:', error);
    throw error;
  }
};

/**
 * Initiates batch email sending for draft RCTIs to suppliers.
 * @param {object} payload - The request payload.
 * @param {string[]} [payload.rctiIds] - Array of specific RCTI document IDs to process.
 * @param {boolean} [payload.sendAllDrafts] - If true, processes all RCTIs with status 'draft'.
 * @returns {Promise<object>} - The response data containing the job ID.
 * @throws {Error} - Throws an error if the API call fails or the user is not authenticated.
 */
export const sendBatchRctiEmails = async (payload) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!payload || (!payload.rctiIds && typeof payload.sendAllDrafts !== 'boolean')) {
    throw new Error("Invalid payload: Must provide either rctiIds or sendAllDrafts.");
  }
  if (payload.rctiIds && !Array.isArray(payload.rctiIds)) {
    throw new Error("Invalid payload: rctiIds must be an array.");
  }
  if (payload.rctiIds && payload.sendAllDrafts) {
    throw new Error("Invalid payload: Cannot specify both rctiIds and sendAllDrafts.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/batch-email-rctis`;

    console.log("Initiating batch RCTI email sending, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    // The API returns 202 Accepted on success
    if (response.status === 202 && responseData.status === 'success') {
      return responseData.data; // Expected: { jobId: "...", message: "..." }
    } else {
      // Handle other non-202 responses as errors, including 4xx and 5xx
      const errorMessage = responseData.message || `Failed to initiate batch RCTI emailing. Status: ${response.status}`;
      console.error("Error initiating batch RCTI email sending:", { status: response.status, responseData });
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Exception in sendBatchRctiEmails:", error);
    // Re-throw the error so it can be caught by the calling component
    // If it's already an Error object with a message, re-throw it directly
    // Otherwise, wrap it in a new Error object
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while trying to send batch RCTI emails.');
  }
};

/**
 * Resends RCTI email for a specific job.
 * @param {string} jobId - The ID of the job to resend RCTI email for.
 * @returns {Promise<object>} - The response data.
 * @throws {Error} - Throws an error if the API call fails or the user is not authenticated.
 */
export const resendRCTIEmail = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!jobId) {
    throw new Error("Job ID is required to resend RCTI email.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/resend-email`;
    const payload = { jobId };

    console.log(`Resending RCTI email for job ID: ${jobId}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (response.ok && responseData.status === 'success') {
      return responseData;
    } else {
      const errorMessage = responseData.message || `Failed to resend RCTI email. Status: ${response.status}`;
      console.error("Error resending RCTI email:", { jobId, status: response.status, responseData });
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error(`Exception in resendRCTIEmail for job ID ${jobId}:`, error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while trying to resend RCTI email.');
  }
};

/**
 * Retrieves the status of a batch RCTI email job.
 * @param {string} jobId - The ID of the batch email job.
 * @returns {Promise<object>} - The job status data.
 * @throws {Error} - Throws an error if the API call fails or the user is not authenticated.
 */
export const getRctiEmailJobStatus = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!jobId) {
    throw new Error("Job ID is required to fetch email job status.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/email-job-status/${jobId}`;

    console.log(`Fetching RCTI email job status for job ID: ${jobId}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        // No 'Content-Type': 'application/json' needed for GET typically, unless API requires it
      },
    });

    const responseData = await response.json();

    if (response.ok && responseData.status === 'success') {
      return responseData.data; // Expected job status object
    } else {
      const errorMessage = responseData.message || `Failed to fetch email job status. Status: ${response.status}`;
      console.error("Error fetching RCTI email job status:", { jobId, status: response.status, responseData });
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error(`Exception in getRctiEmailJobStatus for job ID ${jobId}:`, error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while trying to fetch email job status.');
  }
};

/**
 * Voids an RCTI, optionally voiding it in Xero if already sent.
 * Also reverts the corresponding job status to approved.
 * @param {string} rctiId - The Firestore RCTI ID
 * @returns {Promise<object>} - The response data containing void status
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const voidRCTI = async (rctiId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiId) {
    throw new Error("RCTI ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/void`;
    const payload = { rctiId };

    console.log("Voiding RCTI, payload:", payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to void RCTI');
    }

    return responseData;
  } catch (error) {
    console.error(`Error voiding RCTI ${rctiId}:`, error);
    throw error;
  }
};

/**
 * Generate ABA file for selected RCTIs
 * @param {string[]} rctiIds - Array of RCTI document IDs
 * @returns {Promise<object>} - The response data containing ABA file content and metadata
 * @throws {Error} - Throws an error if the API call fails or the user is not authenticated
 */
export const generateABAFile = async (rctiIds) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!rctiIds || !Array.isArray(rctiIds) || rctiIds.length === 0) {
    throw new Error("RCTI IDs array is required and must not be empty.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/rcti/generate-aba`;
    const payload = { rctiIds };

    console.log(`Generating ABA file for ${rctiIds.length} RCTIs`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (response.ok && responseData.status === 'success') {
      return responseData.data;
    } else {
      const errorMessage = responseData.message || `Failed to generate ABA file. Status: ${response.status}`;
      console.error("Error generating ABA file:", { status: response.status, responseData });
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error("Exception in generateABAFile:", error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while generating ABA file.');
  }
};


/**
 * Create an RCTI that is not tied to a job. The backend generates the PDF, emails the
 * franchisee and pushes to Xero, and reports each step separately, so the caller must read
 * result.pdf / result.email / result.xero rather than assuming success means all three ran.
 */
export const createManualRCTI = async ({ fseId, description, amountExGst, ndisJob = false }) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!fseId) throw new Error("Franchisee is required.");
  if (!description || !description.trim()) throw new Error("Description is required.");
  const amount = Number(amountExGst);
  if (!Number.isFinite(amount) || amount <= 0) throw new Error("Amount must be greater than zero.");

  const idToken = await user.getIdToken();
  const response = await fetch(`${BASE_URL}/rcti/create-manual`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fseId, description: description.trim(), amountExGst: amount, ndisJob }),
  });

  const responseData = await response.json();
  if (!response.ok || responseData.status !== 'success') {
    throw new Error(responseData.message || `Failed to create manual RCTI (HTTP ${response.status})`);
  }
  return responseData;
};
